import Router from 'next/router';
import { useEffect, useRef, useState } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import { Box, Fab, Stack, Typography } from '@mui/material';
import Drawer from '@mui/material/Drawer';
import { useTranslation } from 'next-i18next';
import { useSnackbar } from 'notistack';
import { FormProvider, useForm } from 'react-hook-form';

import ConfirmAllBundleInformation from './confirmAllInformation/bundle';
import EnrollmentStepBar from './enrollmentStepBar';
import ProductCPDEnrollmentFooter from './footer';
import LicenseInformation from './licenseInformation';
import PersonalInformation from './personalInformation';
import SelectRoundBundle from './selectRoundBundle';
import { Container } from './style';

import CpdLicensesConfirmationModal, {
  ConfirmLicenseData,
} from '@components/atoms/dialog/cpdLicensesConfirmationModal';
import cpdAPI from 'src/api/cpd';
import { CPDEnrollmentForm, ProductTypeId, RegulatorType } from 'src/helpers/constants';
import { useMultiStepForm } from 'src/helpers/use';
import { getCPDLicensesByRegulator } from 'src/helpers/utils';
import {
  ApplicantTypeEnum,
  CPDLicenseTypeEnum,
  CPDRegulatorTypeEnum,
  ICPDCourse,
  ICourse,
  IEnrollmentStep,
  LicenseRenewalTypeEnum,
} from 'src/interfaces';
import { ICPDBundleEnrollment } from 'src/interfaces/bundle';
import { IProductDetail } from 'src/interfaces/product';
import { LearningTypeId } from 'src/types/product';

interface Props extends IRenderStep {
  open: boolean;
  onClose: () => void;
  step?: IEnrollmentStep;
  productDetail: IProductDetail;
  isEmailValid: boolean;
  initEnrollmentData: CPDEnrollmentForm | null;
  userId?: number;
}

interface IRenderStep {
  cpdBundleEnrollment: ICPDBundleEnrollment;
  cpdCourse?: ICPDCourse;
  isEmailValid?: boolean;
  initEnrollmentData?: CPDEnrollmentForm | null;
}

enum StepKeyEnum {
  PERSONAL_INFORMATION = 'personalInformation',
  OIC_LICENSE_INFORMATION = 'OICLicenseInfomation',
  TSI_LICENSE_INFORMATION = 'TSILicenseInfomation',
  SELECT_ROUND = 'SelectRound',
  CONFIRMATION = 'Confirmation',
}

const initialStep: IEnrollmentStep = {
  id: 0,
  key: StepKeyEnum.PERSONAL_INFORMATION,
  render: ({
    cpdBundleEnrollment,
    isEmailValid,
    initEnrollmentData,
  }: {
    cpdBundleEnrollment?: ICPDBundleEnrollment;
    isEmailValid: boolean;
    initEnrollmentData: CPDEnrollmentForm;
  }) => (
    <PersonalInformation
      isEmailValid={isEmailValid}
      initEnrollmentData={null}
      citizenId={cpdBundleEnrollment?.citizenId}
      email={cpdBundleEnrollment?.email}
      profile={cpdBundleEnrollment?.profile}
    />
  ),
};

const ProductCPDEnrollment = (props: Props) => {
  const { open, onClose, productDetail, cpdBundleEnrollment } = props;
  const [steps, setSteps] = useState<IEnrollmentStep[]>([initialStep]);

  const [isOpenLicensesConfirmModal, setIsOpenLicensesConfirmModal] = useState<boolean>(false);
  const [confirmLicenseDatas, setConfirmLicenseDatas] = useState<ConfirmLicenseData[]>([]);

  const isConfirmLicensesRef = useRef<boolean>(false);

  const methods = useForm<CPDEnrollmentForm>({
    defaultValues: { prefix: '' },
  });

  const { enqueueSnackbar } = useSnackbar();

  const { t } = useTranslation('common');

  const handleSubmitForm = async (data: CPDEnrollmentForm) => {
    const productId = props.productDetail.id;
    const { citizenId } = data;
    const { userId } = props;

    const fetchEnrollmentRes = await cpdAPI.fetchCPDBundleEnrollment(Number(productId), citizenId);
    const canEnrollToBundle = fetchEnrollmentRes?.data.canEnrollToBundle;

    if (!canEnrollToBundle || !userId) return;

    const cpdProductBundleItems = productDetail.product_bundle_items?.filter(
      (item) => item.product_skus?.products.learning_type_id === LearningTypeId.CPD,
    );

    const cpdCourses = cpdProductBundleItems?.map((item) => item.product_skus?.products.course as ICPDCourse);

    const createTempEnrollmentRes = await cpdAPI.createTemporaryBundleCpdEnrollmentFormData(
      productId,
      userId,
      data,
      cpdCourses,
    );

    if (createTempEnrollmentRes.status === 201) {
      Router.push(`/bundles/${props.productDetail.id_name}/payment`);
    } else {
      setTimeout(() => {
        enqueueSnackbar({ message: t('save_error'), severity: 'error' });
      }, 500);
    }
  };

  const handleLicensesConfirmation = () => {
    if (!confirmLicenseDatas) return;
    isConfirmLicensesRef.current = true;
    setIsOpenLicensesConfirmModal(false);
    handleNext();
  };

  const bindingNextStep = (activeStep: IEnrollmentStep, data: CPDEnrollmentForm) => {
    const isOICLicenseInformationStep = activeStep.key === StepKeyEnum.OIC_LICENSE_INFORMATION;
    const isTSILicenseInformationStep = activeStep.key === StepKeyEnum.TSI_LICENSE_INFORMATION;
    const isLicenseInformationStep = isOICLicenseInformationStep || isTSILicenseInformationStep;

    let availableLicenseAmount = false;

    const cpdProductBundleItems = productDetail.product_bundle_items?.filter(
      (item) => item.product_skus?.products.learning_type_id === LearningTypeId.CPD,
    );

    const cpdCourses = cpdProductBundleItems?.map((item) => item.product_skus?.products.course as ICPDCourse);
    const isLicenseForthPlusValid = cpdCourses.some(
      (item) => item.licenseRenewal === LicenseRenewalTypeEnum.FourthPlusRenewal,
    );

    if (isOICLicenseInformationStep && isLicenseForthPlusValid) {
      const oicLicenses = getCPDLicensesByRegulator(cpdBundleEnrollment.cpdEnrollment, CPDRegulatorTypeEnum.OIC);
      availableLicenseAmount = oicLicenses.length > 0;
    }

    if (isTSILicenseInformationStep) {
      availableLicenseAmount = true;
    }

    if (isLicenseInformationStep && !isConfirmLicensesRef.current && availableLicenseAmount) {
      const confirmLicenseDatasTemp: ConfirmLicenseData[] = [];

      if (isOICLicenseInformationStep && data.isSelectAgentOICLifeLicense && data.oicAgentLifeLicenseNo) {
        confirmLicenseDatasTemp.push({
          regulatorType: RegulatorType.OIC,
          applicantType: ApplicantTypeEnum.AGENT,
          licenseType: CPDLicenseTypeEnum.LIFE,
          licenseNo: data.oicAgentLifeLicenseNo,
        });
      }

      if (isOICLicenseInformationStep && data.isSelectAgentOICNonLifeLicense && data.oicAgentNonLifeLicenseNo) {
        confirmLicenseDatasTemp.push({
          regulatorType: RegulatorType.OIC,
          applicantType: ApplicantTypeEnum.AGENT,
          licenseType: CPDLicenseTypeEnum.NON_LIFE,
          licenseNo: data.oicAgentNonLifeLicenseNo,
        });
      }

      if (isOICLicenseInformationStep && data.isSelectBrokerOICLifeLicense && data.oicBrokerLifeLicenseNo) {
        confirmLicenseDatasTemp.push({
          regulatorType: RegulatorType.OIC,
          applicantType: ApplicantTypeEnum.BROKER,
          licenseType: CPDLicenseTypeEnum.LIFE,
          licenseNo: data.oicBrokerLifeLicenseNo,
        });
      }

      if (isOICLicenseInformationStep && data.isSelectBrokerOICNonLifeLicense && data.oicBrokerNonLifeLicenseNo) {
        confirmLicenseDatasTemp.push({
          regulatorType: RegulatorType.OIC,
          applicantType: ApplicantTypeEnum.BROKER,
          licenseType: CPDLicenseTypeEnum.NON_LIFE,
          licenseNo: data.oicBrokerNonLifeLicenseNo,
        });
      }

      if (isTSILicenseInformationStep && data.tsiLicenseNo) {
        confirmLicenseDatasTemp.push({
          regulatorType: RegulatorType.TSI,
          licenseNo: data.tsiLicenseNo,
        });
      }

      setConfirmLicenseDatas(confirmLicenseDatasTemp);
      setIsOpenLicensesConfirmModal(true);
      return true;
    }

    isConfirmLicensesRef.current = false;

    return false;
  };

  const { activeStep, lastIndex, currentStepNum, handleNext, handleBack, handleStep } = useMultiStepForm(
    [...steps],
    handleSubmitForm,
    bindingNextStep,
  );

  const renderBundleStep = (cpdCourses: ICPDCourse[], regCourses: ICourse[]) => {
    const cpdFormSteps: IEnrollmentStep[] = [];

    const oicCourses = cpdCourses.filter(
      (course) =>
        course.regulator === CPDRegulatorTypeEnum.OIC &&
        course.licenseRenewal !== LicenseRenewalTypeEnum.RequestLicense,
    );

    const tsiCourses = cpdCourses.filter((course) => course.regulator === CPDRegulatorTypeEnum.TSI);

    if (oicCourses.length !== 0) {
      cpdFormSteps.push({
        id: cpdFormSteps.length + 1,
        key: StepKeyEnum.OIC_LICENSE_INFORMATION,
        render: ({ cpdBundleEnrollment }: { cpdBundleEnrollment: ICPDBundleEnrollment }) => {
          const oicLicenses = getCPDLicensesByRegulator(cpdBundleEnrollment.cpdEnrollment, CPDRegulatorTypeEnum.OIC);
          return <LicenseInformation customerLicenses={oicLicenses} cpdCourses={oicCourses} isBundle />;
        },
      });
    }

    if (tsiCourses.length !== 0) {
      cpdFormSteps.push({
        id: cpdFormSteps.length + 1,
        key: StepKeyEnum.TSI_LICENSE_INFORMATION,
        render: ({ cpdBundleEnrollment }: { cpdBundleEnrollment: ICPDBundleEnrollment }) => {
          const tsiLicenses = getCPDLicensesByRegulator(
            cpdBundleEnrollment.cpdEnrollment || [],
            CPDRegulatorTypeEnum.TSI,
          );
          return <LicenseInformation customerLicenses={tsiLicenses} cpdCourses={tsiCourses} isBundle />;
        },
      });
    }

    cpdFormSteps.push({
      id: cpdFormSteps.length + 1,
      key: StepKeyEnum.SELECT_ROUND,
      render: ({ cpdBundleEnrollment }: { cpdBundleEnrollment: ICPDBundleEnrollment }) => (
        <SelectRoundBundle
          cpdBundleEnrollment={cpdBundleEnrollment}
          productDetail={productDetail}
          handleNext={handleNext}
        />
      ),
    });

    cpdFormSteps.push({
      id: cpdFormSteps.length + 1,
      key: StepKeyEnum.CONFIRMATION,
      render: () => <ConfirmAllBundleInformation cpdCourses={cpdCourses} regCourses={regCourses} />,
    });

    setSteps([...steps, ...cpdFormSteps]);
  };

  const renderSteps = () => {
    if (!productDetail) return;
    const isProductCourse = productDetail.product_type_id === ProductTypeId.COURSE;
    const isProductBundle = productDetail.product_type_id === ProductTypeId.BUNDLE;
    if (isProductBundle) {
      const cpdProductBundleItems = productDetail.product_bundle_items?.filter(
        (item) => item.product_skus?.products.learning_type_id === LearningTypeId.CPD,
      );

      const regProductbundleItems = productDetail.product_bundle_items?.filter(
        (item) => item.product_skus?.products.learning_type_id === LearningTypeId.REG,
      );

      const cpdCourses = cpdProductBundleItems?.map((item) => item.product_skus?.products.course as ICPDCourse);
      const regCourses = regProductbundleItems?.map((item) => item.product_skus?.products.course as ICourse);

      renderBundleStep(cpdCourses!, regCourses);
    }
  };

  useEffect(() => {
    if (!open) {
      methods.clearErrors();
    }
  }, [open]);

  useEffect(() => {
    renderSteps();
  }, [productDetail]);

  return (
    <>
      <CpdLicensesConfirmationModal
        open={isOpenLicensesConfirmModal}
        onClose={() => setIsOpenLicensesConfirmModal(false)}
        onConfirm={handleLicensesConfirmation}
        licenses={confirmLicenseDatas}
      />

      <Drawer PaperProps={{ style: { height: '100vh' } }} anchor="bottom" open={open} onClose={onClose}>
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(handleNext)}>
            <Stack flexDirection="column" sx={{ minHeight: '100vh' }}>
              <Container>
                <EnrollmentStepBar
                  steps={steps}
                  activeStep={activeStep.id}
                  handleStep={(step) => {
                    handleStep(step.id);
                  }}
                />
                <Box sx={{ my: { md: '36px', xs: '20px' } }}>
                  <Typography color="primary.main" sx={{ typography: { xs: 'h7', md: 'h4' } }}>
                    {t('step_from_to', { from: currentStepNum, to: steps.length })}
                  </Typography>
                  {activeStep.render({ ...props })}
                </Box>
              </Container>
              <Fab
                onClick={onClose}
                sx={{
                  position: 'fixed',
                  right: { xs: 20, md: 30 },
                  top: { xs: 20, md: 30 },
                  height: { xs: '40px', md: '60px' },
                  width: { xs: '40px', md: '60px' },
                  bgcolor: 'common.white',
                  boxShadow: '0px 3px 6px rgba(0, 0, 0, 0.160784)',
                }}
              >
                <CloseIcon sx={{ color: '#BDBDBD', fontSize: { xs: '20px', md: '36px' } }} />
              </Fab>
              <ProductCPDEnrollmentFooter
                handleBack={handleBack}
                activeStep={activeStep}
                steps={steps}
                lastIndex={lastIndex}
              />
            </Stack>
          </form>
        </FormProvider>
      </Drawer>
    </>
  );
};

export default ProductCPDEnrollment;
